import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, X } from 'lucide-react';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import CustomDateRange from '../crm/charts/CustomDateRange';
import {
  create_manufacturing_order,
  list_manufacturing_orders,
  MANUFACTURING_PRIORITY_LABELS,
  MANUFACTURING_STATUS_LABELS,
} from '../../../utils/manufacturing_services';
import { WonOpportunityPickerBody } from './WonOpportunityPickerModal';
import './Produccion.css';

const statusVariant = {
  0: 'secondary',
  1: 'primary',
  2: 'info',
  3: 'warning',
  4: 'success',
  5: 'danger',
};

const priorityVariant = {
  1: 'danger',
  2: 'secondary',
  3: 'light',
};

const EMPTY_RANGE = [{ startDate: null, endDate: null, key: 'selection' }];

function formatDate(value) {
  if (!value || value === '0000-00-00' || value === '0000-00-00 00:00:00') return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  return d.toLocaleDateString('es-CO');
}

function inSelectedRange(value, range) {
  const selection = range?.[0];
  if (!selection?.startDate || !selection?.endDate) return true;
  if (!value || value === '0000-00-00' || value === '0000-00-00 00:00:00') return false;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;

  const startDate = new Date(selection.startDate);
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(selection.endDate);
  endDate.setHours(23, 59, 59, 999);
  return date >= startDate && date <= endDate;
}

function DateRangeFilterInput({
  label,
  placeholder,
  range,
  onRangeChange,
  isOpen,
  onOpen,
  onClose,
  containerRef,
  positionStyle,
}) {
  const clearRange = (event) => {
    event.stopPropagation();
    onRangeChange([...EMPTY_RANGE]);
  };

  return (
    <div className="position-relative" ref={containerRef}>
      <Form.Label className="small mb-1">{label}</Form.Label>
      <InputGroup onClick={onOpen} style={{ cursor: 'pointer' }}>
        <Form.Control
          readOnly
          value={
            range[0].startDate && range[0].endDate
              ? `${range[0].startDate.toLocaleDateString('es-ES')} - ${range[0].endDate.toLocaleDateString('es-ES')}`
              : placeholder
          }
          style={{
            cursor: 'pointer',
            borderTopRightRadius: range[0].startDate ? 0 : undefined,
            borderBottomRightRadius: range[0].startDate ? 0 : undefined,
          }}
        />
        {range[0].startDate && (
          <Button
            variant="light"
            onClick={clearRange}
            className="d-flex align-items-center border"
            style={{ zIndex: 5 }}
          >
            <i className="bi bi-x-circle-fill" style={{ color: 'grey', fontSize: '1rem' }} />
          </Button>
        )}
      </InputGroup>
      {isOpen && (
        <div
          className="position-absolute shadow-lg bg-white"
          style={{ zIndex: 1050, top: '100%', marginTop: 4, ...positionStyle }}
        >
          <CustomDateRange
            range={range}
            handleDateChange={(item) => {
              const newRange = [item.selection];
              onRangeChange(newRange);
              if (
                newRange[0].startDate &&
                newRange[0].endDate &&
                newRange[0].startDate.getTime() !== newRange[0].endDate.getTime()
              ) {
                onClose();
              }
            }}
            setIsDatePickerOpen={(open) => {
              if (!open) onClose();
            }}
          />
        </div>
      )}
    </div>
  );
}

export default function Produccion({ permission }) {
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const isAdmin = String(permission) === '1';

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [channel, setChannel] = useState('');
  const [creationRange, setCreationRange] = useState(EMPTY_RANGE);
  const [deliveryRange, setDeliveryRange] = useState(EMPTY_RANGE);
  const [creationPickerOpen, setCreationPickerOpen] = useState(false);
  const [deliveryPickerOpen, setDeliveryPickerOpen] = useState(false);
  const [creationPickerPos, setCreationPickerPos] = useState({});
  const [deliveryPickerPos, setDeliveryPickerPos] = useState({});
  const creationPickerRef = useRef(null);
  const deliveryPickerRef = useRef(null);

  const [showModal, setShowModal] = useState(false);
  const [createMode, setCreateMode] = useState('opportunity');
  const [standaloneTitle, setStandaloneTitle] = useState('');
  const [creatingId, setCreatingId] = useState(null);
  const [creatingStandalone, setCreatingStandalone] = useState(false);
  const [error, setError] = useState('');

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    const result = await list_manufacturing_orders(token);
    if (result === 'INVALID') {
      navigate('/');
      return;
    }
    if (!result || result.ok !== true) {
      setError('No se pudieron cargar las órdenes de producción.');
      setOrders([]);
    } else {
      setOrders(Array.isArray(result.orders) ? result.orders : []);
    }
    setLoading(false);
  }, [token, navigate]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (creationPickerRef.current && !creationPickerRef.current.contains(event.target)) {
        setCreationPickerOpen(false);
      }
      if (deliveryPickerRef.current && !deliveryPickerRef.current.contains(event.target)) {
        setDeliveryPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openPicker = (kind) => {
    const ref = kind === 'creation' ? creationPickerRef : deliveryPickerRef;
    const setPos = kind === 'creation' ? setCreationPickerPos : setDeliveryPickerPos;
    const setOpen = kind === 'creation' ? setCreationPickerOpen : setDeliveryPickerOpen;
    if (ref.current) {
      const inputRect = ref.current.getBoundingClientRect();
      const calendarWidth = 650;
      if (inputRect.left + calendarWidth > window.innerWidth) {
        setPos({ right: 0, left: 'auto' });
      } else {
        setPos({ left: 0, right: 'auto' });
      }
    }
    if (kind === 'creation') setDeliveryPickerOpen(false);
    else setCreationPickerOpen(false);
    setOpen(true);
  };

  const hasActiveFilters = useMemo(() => {
    if (search.trim() || status !== '' || priority !== '' || channel.trim()) return true;
    if (creationRange[0].startDate || deliveryRange[0].startDate) return true;
    return false;
  }, [search, status, priority, channel, creationRange, deliveryRange]);

  const filteredOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    const channelQ = channel.trim().toLowerCase();
    return orders.filter((o) => {
      if (status !== '' && String(o.status) !== String(status)) return false;
      if (priority !== '' && String(o.priority) !== String(priority)) return false;
      if (channelQ && !String(o.channel_name || '').toLowerCase().includes(channelQ)) return false;
      if (!inSelectedRange(o.creation_date, creationRange)) return false;
      if (!inSelectedRange(o.expected_finishing_date, deliveryRange)) return false;

      if (!q) return true;
      const hay = [
        o.id,
        o.opportunity_id,
        o.opportunity_name,
        o.title,
        o.channel_name,
        MANUFACTURING_STATUS_LABELS[o.status],
        MANUFACTURING_PRIORITY_LABELS[o.priority],
      ]
        .map((v) => String(v ?? '').toLowerCase())
        .join(' ');
      return hay.includes(q);
    });
  }, [orders, search, status, priority, channel, creationRange, deliveryRange]);

  const clearFilters = () => {
    setSearch('');
    setStatus('');
    setPriority('');
    setChannel('');
    setCreationRange([...EMPTY_RANGE]);
    setDeliveryRange([...EMPTY_RANGE]);
    setCreationPickerOpen(false);
    setDeliveryPickerOpen(false);
  };

  const openCreateModal = () => {
    setCreateMode('opportunity');
    setStandaloneTitle('');
    setShowModal(true);
  };

  const orderDisplayName = (order) => {
    const hasOpp = Number(order.opportunity_id) > 0;
    if (hasOpp) return order.opportunity_name || `Opp #${order.opportunity_id}`;
    return order.title || `Orden #${order.id}`;
  };

  const handleCreateFromOpp = async (opp) => {
    if (!isAdmin || creatingId) return;
    setCreatingId(opp.id);
    const result = await create_manufacturing_order(token, { opportunity_id: opp.id });
    setCreatingId(null);
    if (result === 'INVALID') {
      navigate('/');
      return;
    }
    if (!result || result.ok !== true) {
      window.alert(result?.error === 'already_has_order'
        ? 'Esa oportunidad ya tiene una orden de manufactura.'
        : 'No se pudo crear la orden.');
      return;
    }
    setShowModal(false);
    navigate(`/ProduccionDetail?id=${result.manufacturing_id}`);
  };

  const handleCreateStandalone = async () => {
    if (!isAdmin || creatingStandalone) return;
    const title = standaloneTitle.trim();
    if (!title) {
      window.alert('Escribe un título para la orden.');
      return;
    }
    setCreatingStandalone(true);
    const result = await create_manufacturing_order(token, { title });
    setCreatingStandalone(false);
    if (result === 'INVALID') {
      navigate('/');
      return;
    }
    if (!result || result.ok !== true) {
      window.alert(result?.error === 'invalid_title'
        ? 'El título no es válido.'
        : 'No se pudo crear la orden.');
      return;
    }
    setShowModal(false);
    navigate(`/ProduccionDetail?id=${result.manufacturing_id}`);
  };

  return (
    <div className="produccion-page container-fluid py-3">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <h3 className="mb-0">Producción</h3>
          <div className="text-muted small">Órdenes de manufactura, con o sin oportunidad CRM</div>
        </div>
      </div>

      <div className="produccion-filters mb-3">
        <Row className="g-2 align-items-end">
          <Col xs={12} md={4} lg={3}>
            <Form.Label className="small mb-1">Buscar</Form.Label>
            <InputGroup>
              <InputGroup.Text><Search size={16} /></InputGroup.Text>
              <Form.Control
                placeholder="Orden, título u oportunidad…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col xs={6} md={4} lg={2}>
            <Form.Label className="small mb-1">Estado</Form.Label>
            <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Todos</option>
              {Object.entries(MANUFACTURING_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={6} md={4} lg={2}>
            <Form.Label className="small mb-1">Prioridad</Form.Label>
            <Form.Select value={priority} onChange={(e) => setPriority(e.target.value)}>
              <option value="">Todas</option>
              {Object.entries(MANUFACTURING_PRIORITY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={12} md={4} lg={3}>
            <Form.Label className="small mb-1">Canal</Form.Label>
            <Form.Control
              type="text"
              placeholder="Filtrar por canal…"
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
            />
          </Col>
          <Col xs={12} md="auto" className="ms-md-auto">
            {hasActiveFilters && (
              <Button variant="outline-secondary" size="sm" onClick={clearFilters}>
                <X size={14} className="me-1" />
                Limpiar filtros
              </Button>
            )}
          </Col>
        </Row>
        <Row className="g-2 mt-1">
          <Col xs={12} md={6} lg={4}>
            <DateRangeFilterInput
              label="Fecha creación"
              placeholder="Filtrar por fecha..."
              range={creationRange}
              onRangeChange={setCreationRange}
              isOpen={creationPickerOpen}
              onOpen={() => openPicker('creation')}
              onClose={() => setCreationPickerOpen(false)}
              containerRef={creationPickerRef}
              positionStyle={creationPickerPos}
            />
          </Col>
          <Col xs={12} md={6} lg={4}>
            <DateRangeFilterInput
              label="Fecha entrega"
              placeholder="Filtrar por fecha..."
              range={deliveryRange}
              onRangeChange={setDeliveryRange}
              isOpen={deliveryPickerOpen}
              onOpen={() => openPicker('delivery')}
              onClose={() => setDeliveryPickerOpen(false)}
              containerRef={deliveryPickerRef}
              positionStyle={deliveryPickerPos}
            />
          </Col>
        </Row>
      </div>

      {isAdmin && (
        <button
          type="button"
          className="produccion-fab"
          onClick={openCreateModal}
          title="Nueva orden"
          aria-label="Nueva orden"
        >
          <Plus size={28} strokeWidth={2.5} />
        </button>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : (
        <div className="table-responsive produccion-table-wrap">
          <Table hover className="align-middle mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Orden Producción</th>
                <th>Canal</th>
                <th>Estado</th>
                <th>Prioridad</th>
                <th>Creación</th>
                <th>Entrega esperada</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">
                    No hay órdenes de producción.
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr
                    key={order.id}
                    className="produccion-row"
                    onClick={() => navigate(`/ProduccionDetail?id=${order.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{order.id}</td>
                    <td>
                      <div className="fw-semibold">{orderDisplayName(order)}</div>
                      {Number(order.opportunity_id) > 0 ? (
                        <div className="text-muted small">ID opp: {order.opportunity_id}</div>
                      ) : (
                        <Badge bg="light" text="dark">Sin oportunidad</Badge>
                      )}
                    </td>
                    <td>{order.channel_name || '—'}</td>
                    <td>
                      <Badge bg={statusVariant[order.status] || 'secondary'}>
                        {MANUFACTURING_STATUS_LABELS[order.status] || order.status}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={priorityVariant[order.priority] || 'secondary'} text={Number(order.priority) === 3 ? 'dark' : undefined}>
                        {MANUFACTURING_PRIORITY_LABELS[order.priority] || order.priority}
                      </Badge>
                    </td>
                    <td>{formatDate(order.creation_date)}</td>
                    <td>{formatDate(order.expected_finishing_date)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      )}

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
        dialogClassName="produccion-create-modal"
        contentClassName="produccion-create-modal__content"
      >
        <Modal.Header closeButton className="produccion-create-modal__header">
          <Modal.Title className="produccion-create-modal__title">
            Nueva orden de manufactura
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="produccion-create-modal__body">
          <div className="produccion-create-modal__modes" role="tablist">
            <button
              type="button"
              role="tab"
              className={`produccion-create-modal__mode${createMode === 'opportunity' ? ' is-active' : ''}`}
              aria-selected={createMode === 'opportunity'}
              onClick={() => setCreateMode('opportunity')}
            >
              Desde oportunidad
            </button>
            <button
              type="button"
              role="tab"
              className={`produccion-create-modal__mode${createMode === 'standalone' ? ' is-active' : ''}`}
              aria-selected={createMode === 'standalone'}
              onClick={() => setCreateMode('standalone')}
            >
              Sin oportunidad
            </button>
          </div>
          {createMode === 'opportunity' ? (
            <>
              <p className="produccion-create-modal__hint">
                Selecciona una oportunidad en estado <strong>WON</strong> que aún no tenga orden de manufactura.
              </p>
              {showModal && (
                <WonOpportunityPickerBody
                  token={token}
                  busyId={creatingId}
                  onSelect={handleCreateFromOpp}
                  onInvalidSession={() => navigate('/')}
                />
              )}
            </>
          ) : (
            <>
              <p className="produccion-create-modal__hint">
                Crea una orden con un título. Luego podrás agregar productos del catálogo desde el detalle.
              </p>
              <Form.Label className="small mb-1">Título</Form.Label>
              <Form.Control
                value={standaloneTitle}
                onChange={(e) => setStandaloneTitle(e.target.value)}
                placeholder="Ej. Stock interno — TOMI 8"
                maxLength={255}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreateStandalone();
                  }
                }}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="produccion-create-modal__footer">
          <Button variant="outline-secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
          {createMode === 'standalone' && (
            <Button
              variant="primary"
              onClick={handleCreateStandalone}
              disabled={creatingStandalone || !standaloneTitle.trim()}
            >
              {creatingStandalone ? <Spinner animation="border" size="sm" /> : 'Crear orden'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}
